import { Component } from 'preact';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  DIALOG_STYLE, PANEL_BG, GOLD_BTN, FourCorners,
} from '../lib/medievalTheme';
import MarkdownNotes from './MarkdownNotes';
import { isWeb } from '../utils/platform';
import { t } from '../localization/t';

// One modal, two branches:
//   - standard READY_TO_INSTALL → release notes + Restart Now
//   - server-forced (versionRejection set) → wait UI with progress,
//     Restart Now once READY_TO_INSTALL, or a Retry button if the
//     download stalled (UP_TO_DATE / ERROR after a re-check).
// Same shell and CTA styling regardless of which path the player hit.

const PANEL_BOX_STYLE = {
  background: PANEL_BG,
  border: `1px solid ${GOLD} 0.12)`,
  borderRadius: 'var(--radius-lg)',
  color: TEXT_BODY,
};

const GOLD_CTA_STYLE = { ...GOLD_BTN, borderRadius: 'var(--radius-lg)' };

export default class UpdateModal extends Component {
  state = { applying: false };

  handleRestart = () => {
    this.setState({ applying: true });
    this.props.onApply();
  };

  handleRetry = () => {
    this.props.onRetry?.();
  };

  renderForcedBody() {
    const { updateState, downloadProgress, currentVersion, versionRejection } = this.props;
    const required = versionRejection?.minClientVersion;
    const have = currentVersion || t('unknown');

    // Web has no updater; a redeployed build arrives on page reload.
    // Without this branch the desktop copy below offers "Retry update",
    // which POSTs to an endpoint that doesn't exist. A permanent dead end.
    if (isWeb()) {
      return (
        <>
          <div className="mb-4 px-4 py-3 text-sm leading-relaxed" style={PANEL_BOX_STYLE}>
            <div className="mb-1" style={{ color: TEXT_PRIMARY }}>
              {t('The server requires version')} <strong>{required || t('a newer build')}</strong>.
            </div>
            <div style={{ color: TEXT_MUTED }}>
              {t("You're on {version}. Reload the page to get the latest version.", { version: have })}
            </div>
          </div>
          <div className="text-center">
            <button
              type="button"
              className="px-8 py-3 text-sm font-bold arena-heading cursor-pointer transition-all"
              style={GOLD_CTA_STYLE}
              onClick={() => window.location.reload()}
            >
              {t('Reload')}
            </button>
          </div>
        </>
      );
    }
    const downloading = updateState === 'DOWNLOADING';
    const checking = updateState === 'CHECKING' || updateState === 'UPDATE_AVAILABLE';
    const stalled = !downloading && !checking;
    const progressPct = downloading && Number.isFinite(downloadProgress)
      ? Math.max(0, Math.min(100, Math.round(downloadProgress)))
      : null;

    return (
      <>
        <div className="mb-4 px-4 py-3 text-sm leading-relaxed" style={PANEL_BOX_STYLE}>
          <div className="mb-1" style={{ color: TEXT_PRIMARY }}>
            {t('The server requires version')} <strong>{required || t('a newer build')}</strong>.
          </div>
          <div style={{ color: TEXT_MUTED }}>
            {t("You're on {version}.", { version: have })} {stalled
              ? t("The update download didn't start. Retry below.")
              : t('Downloading the required update now.')}
          </div>
          {progressPct !== null ? (
            <div
              className="mt-3 h-2 w-full overflow-hidden"
              style={{ background: 'rgba(0,0,0,0.4)', borderRadius: 'var(--radius-sm)' }}
            >
              <div
                className="h-full"
                style={{ width: `${progressPct}%`, background: GOLD, transition: 'width 0.3s ease' }}
              />
            </div>
          ) : null}
        </div>
        <div className="text-center">
          {stalled ? (
            <button
              type="button"
              className="px-8 py-3 text-sm font-bold arena-heading cursor-pointer transition-all"
              style={GOLD_CTA_STYLE}
              onClick={this.handleRetry}
            >
              {t('Retry update')}
            </button>
          ) : (
            <div className="text-xs" style={{ color: TEXT_MUTED }}>
              {downloading && progressPct !== null ? `${progressPct}%` : t('Preparing update…')}
            </div>
          )}
        </div>
      </>
    );
  }

  renderReadyBody() {
    const { releaseNotes } = this.props;
    const { applying } = this.state;

    return (
      <>
        {releaseNotes ? (
          <div
            className="mb-6 max-h-64 overflow-y-auto px-4 py-3 text-sm leading-relaxed"
            style={PANEL_BOX_STYLE}
          >
            <div
              className="text-[0.625cqw] font-semibold mb-3"
              style={{ color: `${GOLD} 0.4)` }}
            >
              {t("What's New")}
            </div>
            <MarkdownNotes text={releaseNotes} />
          </div>
        ) : null}

        <div className="text-center">
          <button
            type="button"
            className="px-8 py-3 text-sm font-bold arena-heading cursor-pointer transition-all"
            style={{ ...GOLD_CTA_STYLE, opacity: applying ? 0.6 : 1 }}
            disabled={applying}
            onClick={this.handleRestart}
          >
            {applying ? t('Restarting...') : t('Restart Now')}
          </button>
        </div>
      </>
    );
  }

  render() {
    const { newVersion, updateState, versionRejection } = this.props;
    const headingVersion = newVersion || versionRejection?.minClientVersion;
    const isReady = updateState === 'READY_TO_INSTALL';
    const showForcedWaitBody = versionRejection && !isReady;

    return (
      <div
        className="fixed inset-0 z-[300] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(6px)' }}
      >
        <div className="relative w-full max-w-[32cqw] p-8" style={DIALOG_STYLE}>
          <FourCorners />

          <div className="text-center mb-6">
            <div
              className="text-[0.625cqw] font-semibold mb-2"
              style={{ color: `${GOLD} 0.6)`, textShadow: `0 0 12px ${GOLD} 0.15)` }}
            >
              {t('Update Required')}
            </div>
            <h2
              className="text-xl font-bold arena-heading"
              style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
            >
              {headingVersion ? t('Version {version} is Ready', { version: headingVersion }) : t('A New Version is Ready')}
            </h2>
          </div>

          {showForcedWaitBody ? this.renderForcedBody() : this.renderReadyBody()}
        </div>
      </div>
    );
  }
}
